<template>
  <div class="level status-message" v-if="statusMessage && statusMessage.length > 0">
    <div class="level-item">{{statusMessage}}</div>
  </div>

  <div class="page-header level">
    <div class="level-left">
      <div class='size1-text pri-color'>{{customer.businessName}}</div>
    </div>
    <div class="level-right">
      <button :disabled="!isReady" @click="togglePPAccountStatus" class="button is-primary">
        {{activeButtonText}}
      </button>
    </div>
  </div>
  <div class="breadcrumb-wrapper">
    <router-link :to="breadCrumbLinkDest" class="pri-color breadcrumb">
      &lt; {{breadCrumbLabel}}
    </router-link>
  </div>
  <div class="tile is-ancestor scrollable-section">
    <div class="tile is-6">
      <div class="cust-det-section">
        <div class="sec-bg cust-det-sect-header size2-text">
          Business Information
        </div>
        <div class="cust-det-sec-content">
          <div>
            <span class="cust-det-lbl">Contact Name:</span>
            <span>{{customer.contactName}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Business Name:</span>
            <span>{{customer.businessName}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Business Phone:</span>
            <span>{{customer.businessPhone}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Email:</span>
            <span>{{customer.email}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">EIN Number:</span>
            <span>{{customer.ein}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">EFIN Number:</span>
            <span>{{customer.efin}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">PTIN Number:</span>
            <span>{{customer.ptin}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Address Type:</span>
            <span>{{customer.addressType}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Account Type:</span>
            <span>{{customer.accountType}}</span>
          </div>
          <div class="divider"></div>
        </div>

        <div class="cust-det-sec-content">
          <div>
            <span class="cust-det-lbl">Business Address:</span>
            <span>{{customer.line1}}</span>
            <span>{{customer.line2}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">City:</span>
            <span>{{customer.city}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">State:</span>
            <span>{{customer.stateOrProvince}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Zip:</span>
            <span>{{customer.postalCode}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Country:</span>
            <span>{{customer.country}}</span>
          </div>
        </div>
      </div>

    </div>

    <div class="tile is-6">
      <div class="cust-det-section">
        <div class="level sec-bg ">
          <div class="level-left">
            <div class="cust-det-sect-header size2-text">
              Billing Information
            </div>
          </div>
          <div class="level-right">
            <div class="cust-det-sect-header size2-text right-header-action"
                v-show="!customer.adjustmentAmount > 0"
                v-on:click="launchEditAdjustmentModal()">
              + Add Discount or Fee
            </div>
          </div>
        </div>

        <div class="cust-det-sec-content">
          <div class="columns">
            <div class="column">
              <div class="cust-det-lbl-lg">Unit Cost:</div>
              <div class="large-text">{{unitCostFormatted}}</div>
              <a class="cust-det-lbl-lg actionable-link" @click="launchUpdateUnitCostModal">
                Update Unit Cost
              </a>
            </div>
            <div class="column">
              <div class="cust-det-lbl-lg">Upcoming Cycle Fees:</div>
              <div class="large-text">{{totalAmtDueFormatted}}</div>
              <div v-show="customer.adjustmentAmount > 0">
                <span class="app-red">
                  {{adjAmtFormatted}} {{customer.adjustmentType}} Applied
                </span>
                <a class="cust-det-lbl-lg actionable-link" @click="launchEditAdjustmentModal">
                  Edit
                </a>
              </div>
            </div>
          </div>
          <div class="divider"></div>
          <div>
              <div class="cust-det-lbl-lg">Past Due Fees:</div>
              <div :class="{ 'app-red': isPastDue }" class="large-text">
                {{formatAsMoney(customer.totalAmtPastDue)}}
              </div>
          </div>
          <div class="divider"></div>
        </div>
        <div class="cust-det-sec-content">
          <div class="columns">
            <div class="column">
              <div class="cust-det-lbl-lg">Last Billing Statement Issued:</div>
              <div class="large-text">{{mostRecentProcessDate}}</div>
              <a v-if="hasAtLeastOnePastBill" v-on:click="downloadMostRecentDoc()"
                class="cust-det-lbl-lg actionable-link">
                Download Statement
              </a>
            </div>
            <div class="column">
              <div class="cust-det-lbl-lg">Returns:</div>
              <div>Current Cycle: <span>{{customer.currentBillableTR}}</span></div>
              <div>Lifetime: <span>{{customer.lifetimeBillableTR}}</span></div>
              <div class="cust-det-lbl-lg">Billing Status:</div>
              <div>{{customer.billingStatus}}</div>
              <a class="cust-det-lbl-lg actionable-link" @click="launchUpdateBillStatusModal">
                Update Bill Status
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="tile is-ancestor">
    <div class="tile">
      <div class="cust-det-section">
        <div class="sec-bg cust-det-sect-header size2-text">Credit Card Information</div>
        <div class="cust-det-sec-content">
          <div class="tile">
            <div class="tile is-vertical is-parent is-8">
              <div>
                <span class="cust-det-lbl">Credit Card Type:</span>
                <span>{{customer.cardType}}</span>
              </div>
              <div>
                <span class="cust-det-lbl">Cardholder Name:</span>
                <span>{{cardHolderName}}</span>
              </div>
              <div>
                <span class="cust-det-lbl">Card #:</span>
                <span>{{cardNumberFormatted}}</span>
              </div>
              <div>
                <span class="cust-det-lbl">Expiration:</span>
                <span>{{expirationDateFormatted}}</span>
              </div>
            </div>
            <div class="tile is-parent is-4">
              <div v-if="customer.cardStatus && customer.cardStatus.length > 1"
                class="warning-wrapper">
                <div :class="deriveCardMessageClass">
                  {{customer.cardStatus}}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="tile">
      <div class="cust-det-section">
        <div class="sec-bg cust-det-sect-header size2-text">Billing Address</div>
        <div class="cust-det-sec-content">
          <div>
            <span class="cust-det-lbl">Billing Address:</span>
            <span>{{customer.billingStreet}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">City:</span>
            <span>{{customer.billingCity}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">State:</span>
            <span>{{customer.billingState}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Zip:</span>
            <span>{{customer.billingZip}}</span>
          </div>
          <div>
            <span class="cust-det-lbl">Country:</span>
            <span>{{customer.billingCountry}}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="tile is-ancestor">
    <div class="tile is-12 full-row-tile">
      <div class="cust-det-section-full">
          <div class="sec-bg cust-det-sect-header size2-text">
            Past Receipts and Invoices
          </div>
          <div class='billing-grid-wrapper'>
            <div class="level">
              <div class="level-left">
                <div class="page-heading-label level-item">
                    Per Page:
                </div>
                <div class="level-item">
                  <select v-on:change="onPageSizeChanged()" id="page-size">
                      <option value="10" selected="">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="200">200</option>
                  </select>
                </div>
              </div>
              <div class="level-right">
                <div class="level-item">
                  <button id="export-button" v-on:click="onBtExport()" class="button is-primary">
                    Export to CSV
                  </button>
                </div>
              </div>
            </div>
            <ag-grid-vue
                style="width: 100%; height: 600px;"
                class="ag-theme-alpine"
                id="pastBillRuns"
                :gridOptions="gridOptions"
                @grid-ready="onGridReady"
                :columnDefs="columnDefs"
                :pagination="true"
                :paginationPageSize="paginationPageSize"
                :defaultColDef="defaultColDef"
                :modules="modules"
                :rowData="rowData">
            </ag-grid-vue>
          </div>
      </div>
    </div>
  </div>

  <div class="tile is-ancestor">
    <div class="tile is-12 full-row-tile">
      <div class="cust-det-section-full">
          <div class="level sec-bg ">
            <div class="level-left">
              <div class="cust-det-sect-header size2-text">
                Notes
              </div>
            </div>
            <div class="level-right">
              <div class="cust-det-sect-header size2-text right-header-action"
                  v-on:click="launchAddNoteModal()">
                + Add Note
              </div>
            </div>
          </div>
          <div class='billing-grid-wrapper'>
            <div class="level">
              <div class="level-left">
                <div class="page-heading-label level-item">
                    Per Page:
                </div>
                <div class="level-item">
                  <select v-on:change="onNotesPageSizeChanged()" id="notes-page-size">
                      <option value="10" selected="">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="200">200</option>
                  </select>
                </div>
              </div>
              <div class="level-right">
                <div class="level-item">
                <button id="export-button" v-on:click="onNotesBtExport()" class="button is-primary">
                    Export to CSV
                </button>
                </div>
              </div>
            </div>
            <ag-grid-vue
                style="width: 100%; height: 600px;"
                class="ag-theme-alpine"
                id="notes"
                :gridOptions="notesGridOptions"
                @grid-ready="onNotesGridReady"
                :columnDefs="notesColumnDefs"
                :pagination="true"
                :paginationPageSize="notesPaginationPageSize"
                :defaultColDef="defaultColDef"
                :modules="modules"
                :rowData="notesData">
            </ag-grid-vue>
          </div>
      </div>
    </div>
  </div>

  <div :class="{'is-active': showUpdateUnitCost}" class="modal">
    <div class="modal-background"></div>
    <div class="modal-content">
      <div class="modal-content-box">
        <div class="confirmation-title-bar">Edit Unit Cost</div>

        <div class="modal-content-wrapper">
          <div v-if="errorMessage && errorMessage.length > 0" class="validation-errors">
            <p class="error-msg">{{errorMessage}}</p>
          </div>

          <div class="field">
            <label class="label">Unit Cost<span class="required">*</span></label>
            <div class="control">
              <input class="input" type="number" min=0 max=9999 step=0.01
                v-on:change="formatUpdatedUnitCost" placeholder="0.00"
                v-model="updatedUnitCost">
            </div>
          </div>
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <button :disabled="!isRequiredUCDataProvided" @click="updatePreparerUnitCost"
                  class="send-verify-button button is-primary">
                  Save Change
                </button>
              </div>
            </div>
            <div class="level-right">
              <button @click="cancelUpdateUnitCostClicked"
                  class="cancel-button button is-secondary">
                  Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div :class="{'is-active': showUpdateBillStatus}" class="modal">
    <div class="modal-background"></div>
    <div class="modal-content">
      <div class="modal-content-box">
        <div class="confirmation-title-bar">Edit Bill Status</div>

        <div class="modal-content-wrapper">
          <div v-if="errorMessage && errorMessage.length > 0" class="validation-errors">
            <p class="error-msg">{{errorMessage}}</p>
          </div>

          <div class="field">
            <label class="label">Bill Status<span class="required">*</span></label>
            <div class="control select">
              <select v-model="updatedBillingStatus">
                <option v-for="bs in billingStatuses"
                  :key="bs.BillingStatusID" :value="bs.BillingStatusID">
                  {{bs.Status}}
                </option>
              </select>
            </div>
          </div>
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <button @click="updatePreparerBillingStatus"
                  class="send-verify-button button is-primary">
                  Save Change
                </button>
              </div>
            </div>
            <div class="level-right">
              <button @click="cancelUpdateBillingStatusClicked"
                  class="cancel-button button is-secondary">
                  Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div :class="{'is-active': showEditAdj}" class="modal">
    <div class="modal-background"></div>
    <div class="modal-content">
      <div class="modal-content-box">
        <div class="confirmation-title-bar">Add/Edit Adjustment</div>
        <div class="modal-content-wrapper">
          <div v-if="errorMessages && errorMessages.length > 0" class="validation-errors">
            <ul id="array-rendering">
              <li v-for="errorMsg in errorMessages" :key="errorMsg">
                {{ errorMsg }}
              </li>
            </ul>
          </div>
          <div class="field">
            <label class="label">Discount or Fee?<span class="required">*</span></label>
            <div class="modal-select select">
              <select v-model="updatedAdjType">
                <option value="Fee">Fee</option>
                <option value="Discount">Discount</option>
              </select>
            </div>
          </div>

          <div class="field">
            <label class="label">Enter Dollar Amount<span class="required">*</span></label>
            <div class="control">
              <input class="input" type="number" min=0.01 max=9999 step=0.01
                v-on:change="formatUpdatedAdjAmount" placeholder="0.00"
                v-model="updatedAdjAmount" v-on:input="onInputAdjAmount"
                v-on:blur="onInputAdjAmount">
            </div>
          </div>
          <div class="field">
            <label class="label">Note<span class="required">*</span></label>
            <textarea class="textarea" maxlength="75"
              placeholder="Enter Note (Max 75 Characters)" v-model="updatedAdjNote"
              v-on:input="onInputAdjNote" v-on:blur="onInputAdjNote">
            </textarea>
          </div>
          <div class="has-text-centered modal-buttons">
              <button v-show="customer.adjustmentAmount > 0" @click="removeAdjClicked"
                class="send-verify-button button remove-button">
                Remove
              </button>

              <button :disabled="!isRequiredAdjDataProvided" @click="saveEditAdjClicked"
                class="send-verify-button button is-primary">
                Save Change
              </button>
              <p class="bail-out-message">
                <a href='#' @click="cancelEditAdjClicked">Cancel</a>
              </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div :class="{'is-active': fullNote.length > 0}" class="modal">
    <div class="modal-background"></div>
    <div class="modal-content">
      <div class="modal-content-box">
        <div class="modal-content-wrapper">
          <div class="field">
            <label class="label">Note</label>
            <div class="full-note control">
              {{fullNote}}
            </div>
          </div>
          <div class="level">
            <div class="level-left">
              <div class="level-item">
              </div>
            </div>
            <div class="level-right">
              <button @click="hideNoteModal"
                  class="cancel-button button is-secondary">
                  Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <add-note
    :userId="id.toString()"
    :businessName="customer.businessName"
    :showModal="showAddNote"
    @saved="saveNoteClicked"
    @cancelled="cancelAddNoteClicked">
  </add-note>

  <PageLoading :showLoading="!isReady"></PageLoading>

</template>

<script>
import { mapState } from 'vuex';
import { AgGridVue } from 'ag-grid-vue3';
import { AllCommunityModules } from 'ag-grid-community';
import BillData from '@/services/bill-data';
import AddNote from '@/components/add-note.vue';
// import { format } from 'date-fns';

import PageLoading from '@/components/page-loading.vue';
import MoneyUtils from '@/shared/money-utils';
import GridUtils from '@/shared/grid-utils';

window.currencyFormatter = function currencyFormatter(params) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(params.value);
};

window.dateFormatter = function dateFormatter(params) {
  const dateAsString = params.value;
  const dateParts = dateAsString && dateAsString.length > 0 ? dateAsString.split('-') : '';
  return (dateParts && dateParts.length >= 3)
    ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2]).toLocaleDateString() : '';
};

window.dateTimeFormatter = function dateTimeFormatter(params) {
  // const localVal = new Date(params.value);
  const localVal = new Date(params.value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
  return localVal;
  // return format(localVal, 'MM/dd/yy HH:mm:ss');
};

export default {
  name: 'CurrentBilling',
  components: {
    PageLoading,
    AgGridVue,
    AddNote,
  },
  data() {
    return {
      isLoading: true,
      mostRecentBillRun: '',
      mostRecentFileName: 'receipt.pdf',
      mostRecentProcessDate: '',
      gridOptions: null,
      notesGridOptions: null,
      gridApi: null,
      notesGridApi: null,
      columnApi: null,
      notesColumnApi: null,
      columnDefs: null,
      notesColumnDefs: null,
      defaultColDef: null,
      modules: AllCommunityModules,
      rowData: null,
      notesData: null,
      paginationPageSize: null,
      notesPaginationPageSize: null,
      updatedUnitCost: 0,
      updatedBillingStatus: '',
      showUpdateUnitCost: false,
      showUpdateBillStatus: false,
      showAddNote: false,
      showEditAdj: false,
      errorMessage: '',
      breadCrumbLabel: '',
      breadCrumbLinkDest: '',
      statusMessage: '',
      fullNote: '',
      updatedAdjType: 'Discount',
      updatedAdjAmount: 0,
      updatedAdjNote: '',
      adjNoteIsDirty: false,
      adjAmountIsDirty: false,
      errorMessages: [],
    };
  },
  mixins: [MoneyUtils, GridUtils],
  computed: {
    ...mapState('customers', ['customer', 'billingStatuses']),
    isReady() {
      return this.customer && this.customer.businessName && this.customer.businessName.length > 0;
    },
    isRequiredUCDataProvided() {
      return this.updatedUnitCost
      && !Number.isNaN(this.updatedUnitCost)
      && this.updatedUnitCost >= 0;
    },
    deriveCardMessageClass() {
      if (this.customer.cardStatus && this.customer.cardStatus.length > 1) {
        return this.customer.cardStatus === 'Expired' ? 'app-red' : 'app-yellow';
      }
      return '';
    },
    isRequiredAdjDataProvided() {
      return (this.updatedAdjType && (this.updatedAdjType === 'Fee' || this.updatedAdjType === 'Discount'))
          && (this.updatedAdjAmount && this.updatedAdjAmount > 0)
          && (this.updatedAdjNote && this.updatedAdjNote.length > 0);
    },
    hasAtLeastOnePastBill() {
      return this.mostRecentBillRun && this.mostRecentBillRun.length === 10;
    },
    activeButtonText() {
      return this.customer.isActive ? 'Deactivate' : 'Activate';
    },
    isPastDue() {
      return this.customer.totalAmtPastDue && this.customer.totalAmtPastDue > 0;
    },
    totalAmtPastDueFormatted() {
      return this.formatAsMoney(this.customer.totalAmtPastDue);
    },
    totalAmtDueFormatted() {
      const totAmtSafe = this.customer && this.customer.totalAmtDue
        ? +this.customer.totalAmtDue : 0;
      const totAdjSafe = this.customer && this.customer.adjustmentAmount
        ? +this.customer.adjustmentAmount : 0;
      const res = this.customer && this.customer.adjustmentType === 'Discount'
        ? totAmtSafe - totAdjSafe : totAmtSafe + totAdjSafe;
      return this.formatAsMoney(res);
    },
    cardHolderName() {
      return `${this.customer.billingFirstName}  ${this.customer.billingLastName}`;
    },
    cardNumberFormatted() {
      if (!this.customer.cardNumber || this.customer.cardNumber.length < 4) {
        return '';
      }
      const last4 = this.customer.cardNumber.substr(this.customer.cardNumber.length - 4);
      return `XXXX-XXXX-XXXX-${last4}`;
    },
    expirationDateFormatted() {
      if (!this.customer.expirationDate || this.customer.expirationDate.length < 7) {
        return '';
      }
      const parts = this.customer.expirationDate.split('-');
      return `${parts[1]}/${parts[0]}`;
    },
    unitCostFormatted() {
      return this.formatAsMoney(this.customer.unitCost);
    },
    adjAmtFormatted() {
      return this.formatAsMoney(this.customer.adjustmentAmount);
    },
  },
  async created() {
    const initialCust = {
      unitCost: 0,
      totalAmtDue: 0,
      totalAmtPastDue: 0,
      adjustmentAmount: 0,
    };
    this.$store.commit('customers/setCustomer', initialCust);
    await this.$store.dispatch('customers/fetchCustomer', this.id);
    this.deriveBreadcrumb();
    // console.log('the customer');
    // console.log(this.customer);
    this.updatedUnitCost = this.customer.unitCost;
    this.updatedBillingStatus = `${this.customer.billingStatusId}`;
    this.rowData = this.customer.pastBillings;
    this.notesData = this.customer.notes;
    this.updatedAdjNote = this.customer.adjustmentNote;
    this.updatedAdjAmount = this.customer.adjustmentAmount;
    this.updatedAdjType = this.derivedAdjType();
    if (this.customer.pastBillings && this.customer.pastBillings.length > 0) {
      this.mostRecentBillRun = this.customer.pastBillings[0].ToDate;
      this.mostRecentFileName = this.customer.pastBillings[0].DocType === 'Receipt'
        ? 'receipt.pdf' : 'invoice.pdf';
      // I'm reusing a window level formatter I use for the grid on this same page.
      // eslint-disable-next-line no-undef
      this.mostRecentProcessDate = dateFormatter(
        { value: this.customer.pastBillings[0].ProcessDate },
      );
    }
  },
  beforeMount() {
    this.gridOptions = {};
    this.notesGridOptions = {};

    this.columnDefs = [
      {
        field: 'InvoiceNumber',
        headerName: 'Inv/Rec #',
        width: 15,
      },
      {
        field: 'FromDate',
        headerName: 'From Date',
        filter: 'agDateColumnFilter',
        filterParams: GridUtils.dateFilterParams,
        // eslint-disable-next-line no-undef
        valueFormatter: dateFormatter,
      },
      {
        field: 'ToDate',
        headerName: 'To Date',
        filter: 'agDateColumnFilter',
        filterParams: GridUtils.dateFilterParams,
        // eslint-disable-next-line no-undef
        valueFormatter: dateFormatter,
      },
      {
        field: 'ReturnsBilled',
        headerName: '# Returns',
        filter: 'agNumberColumnFilter',
        filterParams: GridUtils.numberFilterParams,
      },
      {
        field: 'UnitCost',
        headerName: 'Unit Cost',
        filter: 'agNumberColumnFilter',
        filterParams: GridUtils.numberFilterParams,
        // eslint-disable-next-line no-undef
        valueFormatter: currencyFormatter,
      },
      {
        field: 'TotalAmount',
        headerName: 'Amount',
        filter: 'agNumberColumnFilter',
        filterParams: GridUtils.numberFilterParams,
        // eslint-disable-next-line no-undef
        valueFormatter: currencyFormatter,
      },
      {
        field: 'VINCorrections',
        headerName: '# VIN Corr',
        filter: 'agNumberColumnFilter',
        filterParams: GridUtils.numberFilterParams,
      },
      {
        field: 'ProcessDate',
        headerName: 'Process Date',
        filter: 'agDateColumnFilter',
        filterParams: GridUtils.dateFilterParams,
        minWidth: 140,
        // eslint-disable-next-line no-undef
        valueFormatter: dateFormatter,
      },
      {
        field: 'DocType',
        headerName: 'Doc Type',
      },
      {
        field: 'Id',
        headerName: '',
        sortable: false,
        filter: false,
        minWidth: 50,
        cellRenderer: (params) => {
          const wrap = document.createElement('div');

          const sep = document.createElement('span');
          sep.innerText = ' | ';
          const resendLink = document.createElement('a');
          resendLink.href = '#';
          resendLink.innerText = 'Resend';
          resendLink.addEventListener('click', async (e) => {
            e.preventDefault();
            const billTo = params.data.ToDate;
            const { userId } = this.customer;
            const docType = params.data.DocType === 'Invoice' ? 'invoice' : 'receipt';
            const users = [
              {
                userId,
                billTo,
                docType,
              },
            ];
            const res = await BillData.methods.resendDocuments(users);
            if (res.criticalError > 0) {
              const firstPart = 'There was an unanticipated problem in processing your request.';
              const secPart = 'Please contact the IT administrator critical errors: ';
              this.statusMessage = `${firstPart}  ${secPart} ${res.criticalError}`;
            } else {
              this.statusMessage = 'Your request to resend the selected document has been submitted!';
            }
            setTimeout(() => {
              this.statusMessage = '';
            }, 4000);
          });
          const viewLink = document.createElement('a');
          viewLink.href = '#';
          viewLink.innerText = 'View';
          viewLink.addEventListener('click', async (e) => {
            e.preventDefault();
            const id = this.customer.userId;
            const billRun = params.data.ToDate;
            const fn = `${params.data.DocType.toLowerCase()}.pdf`;
            const resp = await BillData.methods.getBillngDocument(id, billRun, fn);

            const fileName = `${id}-${billRun}-${fn}`;
            const fileLink = document.createElement('a');
            fileLink.id = 'file-download';
            fileLink.href = `data:application/pdf;base64,${resp}`;
            fileLink.setAttribute('download', fileName);
            const fdexists = document.getElementById('file-download');
            if (fdexists) {
              document.body.removeChild(document.getElementById('file-download'));
            }
            document.body.appendChild(fileLink);
            fileLink.click();
          });
          wrap.appendChild(resendLink);
          wrap.appendChild(sep);
          wrap.appendChild(viewLink);

          return wrap;
        },
      },
    ];
    this.notesColumnDefs = [
      {
        field: 'Note',
        headerName: 'Note',
        minWidth: 200,
        cellRenderer: (params) => {
          const link = document.createElement('a');
          link.href = '#';
          link.innerText = params.value;
          link.addEventListener('click', (e) => {
            e.preventDefault();
            this.showFullNote(params.data.Note);
          });
          return link;
        },
      },
      {
        field: 'AddedBy',
        headerName: 'Added by',
        editable: true,
        minWidth: 150,
        maxWidth: 250,
      },
      {
        field: 'AddedDate',
        headerName: 'Added',
        filter: 'agDateColumnFilter',
        filterParams: GridUtils.dateFilterParams,
        editable: true,
        maxWidth: 210,
        // eslint-disable-next-line no-undef
        valueFormatter: dateTimeFormatter,
      },
    ];

    this.defaultColDef = {
      editable: false,
      sortable: true,
      flex: 1,
      minWidth: 100,
      filter: 'agTextColumnFilter',
      filterParams: GridUtils.textFilterParams,
      resizable: true,
    };
    this.paginationPageSize = 10;
    this.notesPaginationPageSize = 10;
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.notesGridApi = this.notesGridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.notesGridColumnApi = this.notesGridOptions.columnApi;
  },
  props: {
    id: Number,
    returnTo: String,
    msg: String,
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    async onGridReady(params) {
      //
    },
    // eslint-disable-next-line no-unused-vars
    async onNotesGridReady(params) {
      //
    },
    deriveBreadcrumb() {
      const routePfx = this.returnTo ? this.returnTo.split('/')[0] : 'current-billing';
      const routeDest = this.returnTo ? this.returnTo : 'current-billing';

      switch (routePfx) {
        case 'exception-report':
          this.breadCrumbLabel = 'Back to Exception Report';
          // this.breadCrumbLinkDest = `/${this.returnTo}`;
          break;
        case 'past-billings':
          this.breadCrumbLabel = 'Back to Past Billing Cycle';
          // this.breadCrumbLinkDest = `/${this.returnTo}`;
          break;
        case 'customers':
          this.breadCrumbLabel = 'Back to Customer Search';
          // this.breadCrumbLinkDest = '/customers';
          break;
        default:
          this.breadCrumbLabel = 'Back to Current Billing Cycle';
          break;
      }
      this.breadCrumbLinkDest = `/${routeDest}`;
    },
    onBtExport() {
      const expParams = {
        columnKeys: ['Id', 'FromDate', 'ToDate', 'ReturnsBilled', 'UnitCost', 'TotalAmount', 'VINCorrections', 'ProcessDate', 'DocType'],
      };

      this.gridApi.exportDataAsCsv(expParams);
    },
    onPageSizeChanged() {
      const ps = document.getElementById('page-size').value;
      this.gridApi.paginationSetPageSize(Number(ps));
    },
    onNotesBtExport() {
      // We need the cell callback to format the date (it's in utc 0 format to start with)
      const expParams = {
        processCellCallback: (params) => {
          if (params.column && params.column.colId === 'AddedDate') {
            return window.dateTimeFormatter(params);
          }
          return params.value;
        },
      };

      this.notesGridApi.exportDataAsCsv(expParams);
    },
    onNotesPageSizeChanged() {
      const ps = document.getElementById('notes-page-size').value;
      this.notesGridApi.paginationSetPageSize(Number(ps));
    },

    async togglePPAccountStatus() {
      await this.$store.dispatch('customers/updateCustomerStatus');
    },
    cancelUpdateUnitCostClicked() {
      this.showUpdateUnitCost = false;
    },
    launchUpdateUnitCostModal() {
      this.showUpdateUnitCost = true;
    },
    launchUpdateBillStatusModal() {
      this.showUpdateBillStatus = true;
    },
    async updatePreparerUnitCost() {
      await this.$store.dispatch('customers/updateCustomerUnitCost', this.updatedUnitCost);
      this.showUpdateUnitCost = false;
    },
    async updatePreparerBillingStatus() {
      await this.$store.dispatch('customers/updateCustomerBillingStatus', this.updatedBillingStatus);
      this.showUpdateBillStatus = false;
    },
    cancelUpdateBillingStatusClicked() {
      this.showUpdateBillStatus = false;
    },
    launchAddNoteModal() {
      this.showAddNote = true;
    },
    onInputAdjNote() {
      this.adjNoteIsDirty = true;
      this.validateAdjForm();
    },
    onInputAdjAmount() {
      this.adjAmountIsDirty = true;
      this.validateAdjForm();
    },
    launchEditAdjustmentModal() {
      this.adjNoteIsDirty = false;
      this.adjNoteIsDirty = false;
      this.updatedAdjType = this.derivedAdjType();
      this.formatUpdatedAdjAmount();
      this.showEditAdj = true;
    },
    cancelEditAdjClicked() {
      this.showEditAdj = false;
      this.updatedAdjNote = this.customer.adjustmentNote;
      this.updatedAdjAmount = this.customer.adjustmentAmount;
      this.formatUpdatedAdjAmount();
    },
    async saveEditAdjClicked() {
      const adj = {
        adjustmentType: this.updatedAdjType,
        adjustmentAmount: this.updatedAdjAmount,
        adjustmentNote: this.updatedAdjNote,
      };
      await this.$store.dispatch('customers/updateCustomerAdjustment', adj);
      this.showEditAdj = false;

      await this.$store.dispatch('customers/refreshCustomerNotes', this.id);
      this.notesData = this.customer.notes;
    },
    async removeAdjClicked() {
      this.showEditAdj = false;
      this.updatedAdjAmount = 0;
      this.updatedAdjType = 'Remove';
      this.updatedAdjNote = '';
      this.saveEditAdjClicked();
    },
    async saveNoteClicked() {
      await this.$store.dispatch('customers/refreshCustomerNotes', this.id);
      this.notesData = this.customer.notes;
      this.showAddNote = false;
    },
    cancelAddNoteClicked() {
      this.showAddNote = false;
    },
    async downloadMostRecentDoc() {
      const id = this.customer.userId;
      const billRun = this.mostRecentBillRun;
      const fn = this.mostRecentFileName;
      const resp = await BillData.methods.getBillngDocument(id, billRun, fn);
      const fileName = `${id}-${billRun}-${fn}`;
      const fileLink = document.createElement('a');
      fileLink.id = 'file-download';
      fileLink.href = `data:application/pdf;base64,${resp}`;
      fileLink.setAttribute('download', fileName);
      const fdexists = document.getElementById('file-download');
      if (fdexists) {
        document.body.removeChild(document.getElementById('file-download'));
      }
      document.body.appendChild(fileLink);
      fileLink.click();
    },
    showFullNote(note) {
      this.fullNote = note;
    },
    hideNoteModal() {
      this.fullNote = '';
    },
    formatUpdatedAdjAmount() {
      if (!this.updatedAdjAmount || Number.isNaN(this.updatedAdjAmount)) {
        this.updatedAdjAmount = 0.00;
      } else {
        this.updatedAdjAmount = parseFloat(this.updatedAdjAmount).toFixed(2);
      }
    },
    formatUpdatedUnitCost() {
      this.updatedUnitCost = parseFloat(this.updatedUnitCost).toFixed(2);
    },
    derivedAdjType() {
      // when the field has 'Remove' it should default to Discount in the dropdown still.
      if (!this.customer.adjustmentType) return 'Discount';
      if (this.customer.adjustmentType === 'Fee') return 'Fee';
      return 'Discount';
    },
    validateAdjForm() {
      this.errorMessages = [];
      if ((Number.isNaN(this.updatedAdjAmount) || this.updatedAdjAmount <= 0)
        && this.adjAmountIsDirty) {
        this.errorMessages.push('Adjustment Amount must be > 0');
      }
      if ((!this.updatedAdjNote || this.updatedAdjNote.length === 0)
        && this.adjNoteIsDirty) {
        this.errorMessages.push('A note is required since that will appear on the bill');
      }
    },
  },
};
</script>

<style src='ag-grid-community/dist/styles/ag-theme-alpine.css'></style>
<style src='ag-grid-community/dist/styles/ag-grid.css'></style>
<style lang="scss" scoped>
  .full-row-tile {
    padding-right:12px;
  }
  .large-text {
    font-size: 34px;
    font-weight: 600;
  }
  .column {
    margin-left: 10px;
    padding: 0px;
  }
  .cust-det-sect-header {
    padding-top: 6px;
    padding-bottom: 6px;
    padding-left: 15px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }
  .right-header-action {
    margin-right: 15px;
    cursor: pointer;
  }
  .divider {
    height: 1px;
    width: 95%;
    background-color: #979797;
    margin-top: 10px;
    margin-bottom: 10px;
    margin-right: 10px;
  }
  .cust-det-sec-content {
    margin-top: 10px;
    padding-left: 25px;
    font-size: 1em;
    margin-bottom: 15px;
  }
  .cust-det-lbl {
    font-weight: 600;
    margin-right: 5px;
    line-height: 2;
  }
  .cust-det-lbl-lg {
    font-weight: 600;
    margin-right: 5px;
    margin-top: 15px;
    margin-bottom: 8px;
    font-size: 1em;
  }
  .cust-det-section {
    box-shadow: 0 0 8px 0 rgba(0,0,0,0.21);
    border-radius: 4px;
    margin-bottom: 30px;
    padding-bottom: 20px;
    margin-left: 12px;
    min-width: 95%;
  }
  .cust-det-section-full {
    box-shadow: 0 0 8px 0 rgba(0,0,0,0.21);
    border-radius: 4px;
    margin-left: 12px;
    margin-right: 12px;
    width: 100%;
    min-height: 300px;
    margin-bottom: 20px;
  }
  .warning-wrapper {
    font-size: 1em;
    font-weight: 600;
  }
  .actionable-link {
    line-height: 2;
  }
  .billing-grid-wrapper {
    margin: 20px 25px;
  }
  .modal-background {
    opacity: 0.75;
  }
  .cancel-button {
    min-width: 131px;
  }
  .modal-content-box {
    border-radius: 4px;
    background-color: #FFFFFF;
    box-shadow: 0 0 8px 0 rgba(0,0,0,0.21);
    margin-right: 20px;
    margin-left: 20px;
    margin-bottom: 20px;
    margin-top: 20px;
  }
  .confirmation-title-bar {
    background-color: #FBBB00;
    color: #0F3D80;
    font-size: 18px;
    font-weight: bold;
    min-height: 30px;
    padding-left: 18px;
    padding-top: 5px;
    padding-bottom: 5px;
  }

  .save-note-button {
      margin-top: 30px;
      min-width: 100%;
      border-radius: 8px;
  }
  .modal-content-wrapper {
    padding: 25px 25px;
  }
  .validation-errors {
    color: red !important;
    border-radius: 8px;
    border: 2px solid red;
    padding: 20px;
    margin-bottom: 20px;
  }
  .required {
    color: red;
    margin-left: 5px;
  }
  .error-msg {
    color: #fff;
    margin-left: 15px;
    padding-top: 20px;
    padding-bottom: 20px;
  }
  .status-message {
    background-color: green;
    color: #fff;
    min-height: 50px;
  }
  .modal-buttons > button {
    margin-top: 18px;
    min-width: 100%;
    border-radius: 8px;
  }

  .remove-button {
    color: #fff;
    background-color: #800F0F;
  }
  .bail-out-message {
    margin-top: 18px;
  }
  .modal-select {
    width: 100%;
  }
  .modal-select > select {
    min-width: 100%;
  }
  .full-note {
    overflow-wrap: break-word;
  }
@import "~ag-grid-community/src/styles/ag-grid.scss";
@import "~ag-grid-community/src/styles/ag-theme-alpine/sass/ag-theme-alpine-mixin";
.ag-theme-alpine {
    @include ag-theme-alpine((
        // use theme parameters where possible
        header-foreground-color: #FFF,
        header-background-color: #0F3D80,
        font-family: ('Helvetica', 'Arial', 'sans-serif'),
    ));

    .ag-icon {
      color: #FFF;
    }
    .ag-paging-button > .ag-icon {
        color: #000;
    }
    .ag-header-cell-text {
      font-size: 14px;
    }
}
  @media only screen and (max-width: 1024px) {
    .full-row-tile {
      padding-right:1px;
    }
  }

  @media only screen and (min-width: 1025px) and (max-width: 1500px) {
    .full-row-tile {
      padding-right:1px;
    }
  }

  @media only screen and (min-width: 1501px) and (max-width: 1700px) {
    .full-row-tile {
      padding-right:8px;
    }
  }

</style>
